import { useEffect, useMemo, useState } from 'react';
import { db } from '../../database/firebase';
import {
  fetchEventById,
  fetchStockForEvent,
  fetchSalesForEvent,
  buildSalesDataGrid,
  buildCouponsData,
  buildRemainingPayments,
  computeOrganisationOrVenueSales,
  resolveEventOwners,
  fetchOwnerLinkedLookups,
  buildOwnershipSalesBreakdown,
  computeSettlementNet,
} from '../../services/salesReportService';

export const useSaleReport = ({ eventId, stockId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [stock, setStock] = useState(null);
  const [salesDocs, setSalesDocs] = useState([]);
  const [eventOwners, setEventOwners] = useState([]);
  const [ownershipLookups, setOwnershipLookups] = useState({
    promoterToOwner: new Map(),
  });

  const [salesGrid, setSalesGrid] = useState([]);
  const [couponsGrid, setCouponsGrid] = useState([]);
  const [remainingGrid, setRemainingGrid] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      try {
        setLoading(true);
        const ev = await fetchEventById(db, eventId);
        if (!ev) throw new Error('Event not found');
        const st = await fetchStockForEvent(db, ev, stockId);
        const docs = await fetchSalesForEvent(db, ev.id, stockId);
        const owners = await resolveEventOwners(ev, db);
        const lookups = await fetchOwnerLinkedLookups(owners, docs);
        if (!isMounted) return;
        setEvent(ev);
        setStock(st);
        setSalesDocs(docs);
        setEventOwners(owners);
        setOwnershipLookups(lookups);

        const { grid: salesGridLocal } = buildSalesDataGrid(ev, st, docs);
        const coupons = buildCouponsData(ev);
        const remaining = buildRemainingPayments(st, docs);

        setSalesGrid(salesGridLocal);
        setCouponsGrid(coupons);
        setRemainingGrid(remaining);
        setLoading(false);
      } catch (e) {
        if (!isMounted) return;
        setError(e?.message || 'Failed to load sale report');
        setLoading(false);
      }
    };
    run();
    return () => {
      isMounted = false;
    };
  }, [eventId, stockId]);

  const ownershipBreakdown = useMemo(
    () =>
      buildOwnershipSalesBreakdown(event, salesDocs, eventOwners, ownershipLookups),
    [event, salesDocs, eventOwners, ownershipLookups],
  );

  const totals = useMemo(() => {
    const totalProductSales = salesGrid.reduce(
      (sum, row) => sum + (row.id !== 'total' ? (row.totalSale ?? row.sold * row.price) : 0),
      0,
    );
    const totalCouponDiscount = couponsGrid.reduce(
      (sum, row) => sum + (row.id !== 'total' ? row.totalDiscount || 0 : 0),
      0,
    );
    const totalRemainingAmount = remainingGrid.reduce((sum, row) => {
      if (row.id === 'total') return sum;
      if (row.totalRmainingAmount != null) return sum + Number(row.totalRmainingAmount || 0);
      const remainingPerUnit =
        Number(row.price || 0) - Number(row.downPaymentAmount || 0);
      return sum + remainingPerUnit * Number(row.sold || 0);
    }, 0);
    const orgVenueSales = computeOrganisationOrVenueSales(event, salesDocs);
    const totalRevenue = ownershipBreakdown.totalRevenue || 0;
    const settlementNet = computeSettlementNet(event?.settlementEntries);

    let subTotal = totalProductSales - orgVenueSales - totalCouponDiscount - totalRemainingAmount;
    const isCommissionExtra = event?.isCommissionExtra ? true : false;
    let totalCommissionAmount = 0;
    if (!isCommissionExtra) {
      totalCommissionAmount = (subTotal * (event?.commissionFee || 0)) / 100;
    } else {
      totalCommissionAmount = subTotal * (1 - 100 / (100 + (event?.commissionFee || 0)));
    }
    const totalPayable = subTotal - totalCommissionAmount + settlementNet;

    return {
      totalRevenue,
      totalProductSales,
      totalCouponDiscount,
      totalRemainingAmount,
      orgVenueSales,
      subTotal,
      totalCommissionAmount,
      settlementNet,
      totalPayable,
    };
  }, [salesGrid, couponsGrid, remainingGrid, event, salesDocs, ownershipBreakdown]);

  const setEventData = (next) => setEvent(next);

  return {
    loading,
    error,
    event,
    setEvent: setEventData,
    stock,
    salesGrid,
    couponsGrid,
    remainingGrid,
    totals,
    ownershipBreakdown,
    isSeatedEvent: !!(event && event.type === 3),
  };
};

export default useSaleReport;
